import { ShieldCheck, Baby, UserX } from 'lucide-react';
import { HStatementCheckResult } from '../../services/msdsCoverpageApi';

interface HStatementWarningsProps {
    result: HStatementCheckResult | null;
    hCodes: Array<{ code: string; text: string }>;
}

export function HStatementWarnings({ result, hCodes }: HStatementWarningsProps) {
    if (!result) {
        return (
            <p className="text-sm text-gray-500 italic">
                Upload a MSDS PDF to check H-statements for protection requirements.
            </p>
        );
    }

    const hasWarnings = result.motherProtection || result.youthProtection;

    return (
        <div className="space-y-3">
            {/* H-codes summary */}
            {hCodes.length > 0 && (
                <div className="text-sm">
                    <p className="font-medium text-gray-700 mb-1">Detected H-Statements ({hCodes.length}):</p>
                    <div className="flex flex-wrap gap-1">
                        {hCodes.map(h => (
                            <span
                                key={h.code}
                                title={h.text}
                                className={`px-2 py-0.5 rounded text-xs font-mono ${
                                    result.motherProtectionHCodes.includes(h.code)
                                        ? 'bg-red-100 text-red-700 border border-red-200'
                                        : result.youthProtectionHCodes.includes(h.code)
                                            ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                            : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                {h.code}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Protection warnings */}
            {hasWarnings ? (
                <div className="space-y-2">
                    {result.motherProtection && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <Baby className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-red-800">Mother Protection Required</p>
                                <p className="text-xs text-red-600 mt-1">
                                    ArGSchV SR 822.111.52, Art. 13, Anhang 2
                                </p>
                                <p className="text-xs text-red-600 mt-0.5">
                                    Triggering H-codes: {result.motherProtectionHCodes.join(', ')}
                                </p>
                            </div>
                        </div>
                    )}
                    {result.youthProtection && (
                        <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <UserX className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-orange-800">Youth Protection Required</p>
                                <p className="text-xs text-orange-600 mt-1">
                                    JArSchV SR 822.115, Art. 4
                                </p>
                                <p className="text-xs text-orange-600 mt-0.5">
                                    Triggering H-codes: {result.youthProtectionHCodes.join(', ')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <ShieldCheck className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-700">
                        No mother or youth protection requirements triggered.
                    </p>
                </div>
            )}
        </div>
    );
}
